import React, { useEffect, useState } from 'react';
import { Scanner } from './components/Scanner';
import { History } from './components/History';
import { CreateShipment } from './components/CreateShipment';
import { PasswordProtection } from './components/PasswordProtection';
import { ShipmentHistory } from './components/ShipmentHistory';
import { setupRealtimeSubscription, supabase, createTables } from './lib/supabase'; 
import { useStore } from './store'; 
import { Languages, Plus } from 'lucide-react';
import { translations } from './types';

function App() {
  const syncHistory = useStore(state => state.syncHistory);
  const { language, setLanguage } = useStore();
  const t = translations[language];
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [showCreateShipment, setShowCreateShipment] = useState(false);
  const [showShipmentHistory, setShowShipmentHistory] = useState(false);
  
  // Handle back button
  useEffect(() => {
    const handlePopState = () => {
      setShowCreateShipment(false);
      setShowShipmentHistory(false);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Update history state when showing/hiding CreateShipment
  useEffect(() => {
    if (showCreateShipment) {
      window.history.pushState({ page: 'createShipment' }, '', '/create-shipment');
    } else if (showShipmentHistory) {
      window.history.pushState({ page: 'shipmentHistory' }, '', '/shipment-history');
    } else {
      window.history.pushState({ page: 'home' }, '', '/');
    }
  }, [showCreateShipment, showShipmentHistory]);

  // Check authentication on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsInitializing(true);
        
        // Пытаемся создать таблицы, если они не существуют
        console.log('Creating tables if they do not exist...');
        const { success: tablesCreated, error: tableError } = await createTables();
        
        if (!tablesCreated) {
          console.error('Error creating tables:', tableError);
        } else {
          console.log('Tables created or already exist');
        }
        
        // Включаем отслеживание изменений в реальном времени
        const channel = supabase.channel('custom-all-channel');
        await channel.subscribe();
        
        // Начальная синхронизация
        await syncHistory();
        
        // Настройка подписки на реальное время для таблицы scans_1
        const unsubscribe = setupRealtimeSubscription(() => {
          syncHistory();
        });
        
        return () => {
          channel.unsubscribe();
          unsubscribe();
        };
      } catch (error) {
        console.error('Initialization error:', error);
        setInitError('Ошибка инициализации. Пожалуйста, обновите страницу.');
      } finally {
        setIsInitializing(false);
      }
    };
    
    initializeApp();
  }, [syncHistory]);

  if (!isAuthenticated) {
    return <PasswordProtection onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  const renderContent = () => {
    if (isInitializing) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (initError) {
      return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{initError}</span>
        </div>
      );
    }

    return ( 
      <div className="flex flex-col min-h-screen">
        <Scanner onShowHistory={() => setShowShipmentHistory(true)} />
        <div className="flex-1 overflow-auto px-4 py-4 -mt-16 pt-20 pb-20 relative">
          <History />
        </div>
      </div>
    );
  };
  
  return (
    <div className="app-container flex flex-col min-h-screen">
      <div className="app-content flex-1 overflow-hidden">
        {showCreateShipment ? (
          <CreateShipment onClose={() => setShowCreateShipment(false)} />
        ) : showShipmentHistory ? (
          <ShipmentHistory onClose={() => setShowShipmentHistory(false)} />
        ) : (
          <>
            {renderContent()}
          </>
        )}
      </div>
      
      {/* Language switcher - only shown on scanner page */}
      {!showCreateShipment && !showShipmentHistory && (
        <div className="fixed right-4 bottom-20">
          <button
            onClick={() => setLanguage(language === 'ru' ? 'zh' : 'ru')}
            className="w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Switch Language"
          >
            <Languages className="w-6 h-6" />
          </button>
        </div>
      )}
      
      {/* Floating Action Button */}
      {!showCreateShipment && (
        <button
          onClick={() => setShowCreateShipment(true)}
          className="fixed right-4 bottom-4 w-14 h-14 bg-blue-600 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}

export default App;